import { useState, useEffect, useCallback } from 'react';
import { Button, Input, Modal, Form, Select, message, Pagination } from 'antd';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { apiFetch } from '../api';

const SubDepartments = () => {
  const [subDepartments, setSubDepartments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalDocs, setTotalDocs] = useState(0);

  // Modal states for Add/Edit
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Fetch all parent departments for select dropdown
  const fetchDepartments = async () => {
    try {
      const response = await apiFetch('/api/departments?all=true');
      const payload = await response.json();
      if (response.ok && payload.success && Array.isArray(payload.data)) {
        setDepartments(payload.data);
      }
    } catch (error) {
      console.error('Error loading departments list:', error);
    }
  };

  const fetchSubDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const searchParam = searchTerm.trim();
      const query = `?page=${page}&limit=${limit}${searchParam ? `&search=${encodeURIComponent(searchParam)}` : ''}`;
      const response = await apiFetch(`/api/sub-departments${query}`);
      const payload = await response.json();
      if (response.ok && payload.success) {
        setSubDepartments(payload.data || []);
        setTotalDocs(payload.totalDocs || 0);
      } else {
        message.error(payload.message || 'Failed to load sub-departments.');
      }
    } catch (error) {
      console.error('Error fetching sub-departments:', error);
      message.error('Failed to load sub-departments.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm]);

  useEffect(() => {
    fetchSubDepartments();
    fetchDepartments();
  }, [fetchSubDepartments]);

  const handleOpenAddModal = () => {
    setEditingId(null);
    form.resetFields();
    setShowModal(true);
  };

  const handleOpenEditModal = (record) => {
    setEditingId(record._id);
    form.setFieldsValue({
      name: record.name,
      department: record.department?._id || record.department
    });
    setShowModal(true);
  };

  const handleModalSubmit = async (values) => {
    setSubmitting(true);
    try {
      const url = editingId ? `/api/sub-departments/${editingId}` : '/api/sub-departments';
      const method = editingId ? 'PUT' : 'POST';
      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          department: values.department
        })
      });
      const payload = await response.json();
      if (response.ok && payload.success) {
        message.success(editingId ? 'Sub-department updated successfully.' : 'Sub-department added successfully.');
        setShowModal(false);
        form.resetFields();
        fetchSubDepartments();
      } else {
        message.error(payload.message || 'Failed to save sub-department.');
      }
    } catch (error) {
      console.error('Error saving sub-department:', error);
      message.error('Failed to save sub-department.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id, name) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this sub-department?',
      content: `This action cannot be undone and will delete the sub-department "${name}" from the system.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await apiFetch(`/api/sub-departments/${id}`, {
            method: 'DELETE'
          });
          const payload = await response.json();
          if (response.ok && payload.success) {
            message.success('Sub-department deleted successfully.');
            fetchSubDepartments();
          } else {
            message.error(payload.message || 'Failed to delete sub-department.');
          }
        } catch (error) {
          console.error('Error deleting sub-department:', error);
          message.error('Error occurred while deleting sub-department.');
        }
      }
    });
  };

  return (
    <div className="wm-page">
      {/* Page Header */}
      <div className="wm-page-header">
        <div>
          <h1 className="wm-page-title dark:text-white">Sub-Department</h1>
        </div>
        <div className="flex flex-wrap gap-3 w-full lg:w-auto">
          <Button
            onClick={handleOpenAddModal}
            icon={<FiPlus />}
            size="large"
            type="primary"
            className="wm-btn-primary flex items-center h-10 px-6"
          >
            Add Sub-Department
          </Button>
        </div>
      </div>

      {/* Table Shell */}
      <div className="wm-table-shell dark:bg-slate-900">
        <div className="wm-card-header bg-[var(--wm-surface-muted)] dark:bg-slate-800/20">
          <div className="relative max-w-xs w-full flex gap-2">
            <Input
              size="large"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="w-full rounded-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
              suffix={<FiSearch className="text-slate-400" />}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap w-[50%]">
                  Sub-Department
                </th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap w-[35%]">
                  Department
                </th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap text-center w-[15%]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading ? (
                [...Array(5)].map((_, idx) => (
                  <tr key={idx} className="border-b border-slate-200 dark:border-slate-800">
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-3/4"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-1/2"></div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center space-x-2">
                        <div className="h-8 w-8 bg-slate-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
                        <div className="h-8 w-8 bg-slate-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : subDepartments.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-8 py-14 text-center text-slate-400 font-medium">
                    No sub-departments found matching your search.
                  </td>
                </tr>
              ) : (
                subDepartments.map((subDept) => (
                  <tr key={subDept._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group">
                    <td className="px-4 py-3 text-[13px] font-normal text-slate-800 dark:text-slate-100">
                      {subDept.name}
                    </td>
                    <td className="px-4 py-3 text-[13px] font-normal text-slate-500 dark:text-slate-400">
                      {subDept.department?.name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center space-x-3">
                        <Button
                          onClick={() => handleOpenEditModal(subDept)}
                          type="text"
                          icon={<FiEdit2 className="w-4 h-4" />}
                          className="text-slate-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all border-none"
                        />
                        <Button
                          onClick={() => handleDelete(subDept._id, subDept.name)}
                          type="text"
                          icon={<FiTrash2 className="w-4 h-4" />}
                          className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all border-none"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className="p-4 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
          Total Records Count is {totalDocs}
        </div>
        <Pagination
          current={page}
          pageSize={limit}
          total={totalDocs}
          showSizeChanger
          pageSizeOptions={[10, 25, 50, 100]}
          onChange={(p, ps) => { setPage(p); setLimit(ps); }}
          onShowSizeChange={(current, size) => { setPage(1); setLimit(size); }}
        />
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-vms-navy rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] overflow-hidden border border-slate-100 dark:border-white/10"
            >
              {/* Header */}
              <div className="px-8 py-6 border-b border-slate-100 dark:border-white/5 flex justify-between items-center bg-slate-50/50 dark:bg-white/5">
                <h2 className="text-xl font-medium text-slate-800 dark:text-white">
                  {editingId ? 'Edit Sub-Department' : 'Add Sub-Department'}
                </h2>
                <Button
                  onClick={() => setShowModal(false)}
                  icon={<FiX />}
                  type="primary"
                  className="flex items-center gap-2 px-4 py-2 bg-vms-navy dark:bg-vms-accent text-white dark:text-vms-navy rounded-lg text-xs font-medium transition-all hover:opacity-90 active:scale-95 shadow-md border-none h-10"
                >
                  Close
                </Button>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleModalSubmit}
                className="p-8 space-y-4"
              >
                <Form.Item
                  name="name"
                  label={<span className="font-semibold text-slate-700 dark:text-slate-200">Sub-Department Name <span className="text-rose-500">*</span></span>}
                  rules={[{ required: true, message: 'Sub-department name is required' }]}
                >
                  <Input
                    size="large"
                    placeholder="e.g. Frontend Engineering, Recruiting"
                    className="h-11 rounded-lg border-slate-200"
                  />
                </Form.Item>

                <Form.Item
                  name="department"
                  label={<span className="font-semibold text-slate-700 dark:text-slate-200">Department <span className="text-rose-500">*</span></span>}
                  rules={[{ required: true, message: 'Parent department is required' }]}
                >
                  <Select
                    size="large"
                    placeholder="Select Parent Department"
                    className="h-11"
                    options={departments.map(d => ({ value: d._id, label: d.name }))}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>

                <div className="mt-8 pt-6 border-t border-slate-100 dark:border-white/5 flex justify-end">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    size="large"
                    className="px-8 h-12 bg-vms-navy dark:bg-vms-accent text-white dark:text-vms-navy rounded-xl font-medium uppercase tracking-widest text-xs shadow-xl transition-all border-none"
                  >
                    {editingId ? 'Update Sub-Department' : 'Save Sub-Department'}
                  </Button>
                </div>
              </Form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubDepartments;
